import { SaveEquipmentsDocument } from '~/client/graph/operations/generated/LocationType.generated'
import type {
  IDeliveryHierarchyParentInput,
  IOffloadingEquipmentInput,
} from '~/client/graph/types.generated'
import { LocationType } from '~/client/graph/types.generated'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import {
  compactResourceData,
  mergeResourceData,
  splitResourceFormData,
} from '~/client/src/shared/hooks/splitResourceFormData'
import type InitialState from '~/client/src/shared/stores/InitialState'
import { computeAccessibleLevels } from '~/client/src/shared/utils/accessibleLevels'
import { DEFAULT_SITEMAP_ITEM_COLOR } from '~/client/src/shared/utils/SitemapItemsColors'

import {
  applyEquipmentWorkflowRestriction,
  parseEquipmentResource,
  toEquipmentResourceRecord,
  type EquipmentResource,
  type MissingEquipmentResourceId,
} from './equipmentResource'
import type {
  ProjectResourceFailure,
  ProjectResourceType,
  ResourceSaveCommand,
} from './projectResourceType'
import type { ResourceResult } from './resourceResult'
import { resourceErr, resourceOk } from './resourceResult'

type AccessibleLevelsSource = Pick<InitialState, 'buildings' | 'levels'>

/**
 * What the Equipment builders read from state: the hierarchy maps that accessible levels are
 * computed from, and the site roots an equipment parent is compared against by id. The site
 * roots are optional so a caller holding only the hierarchy maps keeps compiling.
 */
type EquipmentStateSource = AccessibleLevelsSource &
  Partial<Pick<InitialState, 'projectLocations'>>

interface EquipmentFormOverrides {
  readonly templateId?: string | null
  readonly rootFields: Readonly<Record<string, unknown>>
  readonly dataFields: Readonly<Record<string, unknown>>
}

const EQUIPMENT_DEFAULTS: Pick<
  IOffloadingEquipmentInput,
  'color' | 'iconName' | 'name'
> = {
  name: 'New Equipment',
  color: DEFAULT_SITEMAP_ITEM_COLOR,
  iconName: 'equipment',
}

/** Equipment command failure produced when an edit no longer has a record. */
export interface MissingEquipmentResourceRecord {
  readonly _tag: 'MissingEquipmentResourceRecord'
  readonly recordId: string
}

/** Type-specific failures produced while decoding or saving Equipment. */
export type EquipmentResourceDomainFailure =
  | MissingEquipmentResourceId
  | MissingEquipmentResourceRecord

/** Complete expected-failure channel for Equipment resource operations. */
export type EquipmentResourceFailure =
  ProjectResourceFailure<EquipmentResourceDomainFailure>

/**
 * The ids that mean "the project itself" as an equipment parent: the project id - what
 * LocationBase.factory gives a row stored with no parent - and the id of every project
 * site root location in `source`.
 *
 * By id because a parent is named by id alone: the client no longer selects or sends
 * parentType, which was a snapshot of the parent's kind at save time (STX1-597,
 * STX1-1355). The site root is the one kind that cannot drift: neither re-kinding to nor
 * from `project_location` is offered.
 */
export function equipmentProjectRootIds(
  projectId: string | null | undefined,
  source?: Partial<Pick<InitialState, 'projectLocations'>> | null,
): ReadonlySet<string> {
  const ids = new Set<string>()
  if (projectId) ids.add(String(projectId))
  source?.projectLocations?.forEach((_, id) => ids.add(String(id)))

  return ids
}

/**
 * Normalizes a form or record parent into the Equipment mutation shape. The project root
 * means "not parented" for equipment, so it becomes null. It is recognised by id (see
 * equipmentProjectRootIds). The parent is sent as `{ parentId }` only; any parentType the
 * form or record still carries is dropped, because Graph records the parent's kind itself
 * on save (STX1-1355).
 */
export function toEquipmentParentInput(
  parent: unknown,
  projectRootIds: ReadonlySet<string>,
): IDeliveryHierarchyParentInput | null {
  if (!parent || typeof parent !== 'object') return null

  const parentId = 'parentId' in parent ? parent.parentId : null
  if (
    parentId === null ||
    parentId === undefined ||
    parentId === '' ||
    projectRootIds.has(String(parentId))
  ) {
    return null
  }

  return { parentId: String(parentId) }
}

/** Builds one Equipment mutation input without executing effects. */
export function buildEquipmentInput(
  existing: EquipmentResource | null,
  projectId: string,
  recordId: string | null,
  formOverrides: EquipmentFormOverrides,
  accessibleLevelsSource?: EquipmentStateSource | null,
): IOffloadingEquipmentInput {
  const { templateId, rootFields, dataFields } = formOverrides
  const projectRootIds = equipmentProjectRootIds(
    projectId,
    accessibleLevelsSource,
  )
  const parent = toEquipmentParentInput(
    'parent' in rootFields ? rootFields.parent : existing?.parent,
    projectRootIds,
  )

  const input: IOffloadingEquipmentInput = existing
    ? {
        accessibleLevels: [...existing.accessibleLevels],
        assignedGlobes: [...existing.assignedGlobes],
        assignedSitemaps: [...existing.assignedSitemaps],
        code: existing.code,
        color:
          typeof rootFields.color === 'string'
            ? rootFields.color
            : existing.color,
        data: mergeResourceData({ ...existing.data }, { ...dataFields }),
        iconName:
          typeof rootFields.iconName === 'string'
            ? rootFields.iconName
            : existing.iconName,
        id: recordId,
        maxBookingDuration: existing.maxBookingDuration,
        maxOverlappingDeliveries: existing.maxOverlappingDeliveries,
        name:
          typeof rootFields.name === 'string' ? rootFields.name : existing.name,
        operatingIntervals: Array.isArray(rootFields.operatingIntervals)
          ? rootFields.operatingIntervals.map(
              ({ startDate, endDate, isDaily, dayOfWeek }) => ({
                startDate,
                endDate,
                isDaily,
                dayOfWeek,
              }),
            )
          : existing.operatingIntervals.map(interval => ({ ...interval })),
        parent,
        permittedCompanies: Array.isArray(rootFields.permittedCompanies)
          ? rootFields.permittedCompanies
          : [...existing.permittedCompanies],
        projectId,
        templateId: templateId ?? existing.templateId,
      }
    : {
        ...EQUIPMENT_DEFAULTS,
        projectId,
        name:
          typeof rootFields.name === 'string'
            ? rootFields.name
            : EQUIPMENT_DEFAULTS.name,
        color:
          typeof rootFields.color === 'string'
            ? rootFields.color
            : EQUIPMENT_DEFAULTS.color,
        iconName:
          typeof rootFields.iconName === 'string'
            ? rootFields.iconName
            : EQUIPMENT_DEFAULTS.iconName,
        parent,
        templateId: templateId ?? null,
        data: compactResourceData({ ...dataFields }),
      }

  const newParentId = input.parent?.parentId ?? null
  const oldParentId =
    toEquipmentParentInput(existing?.parent, projectRootIds)?.parentId ?? null
  const parentChanged = 'parent' in rootFields && newParentId !== oldParentId

  if (
    parentChanged &&
    accessibleLevelsSource?.buildings &&
    accessibleLevelsSource.levels
  ) {
    input.accessibleLevels = computeAccessibleLevels(
      newParentId,
      accessibleLevelsSource.buildings,
      accessibleLevelsSource.levels,
    )
  }

  return input
}

/** Decodes the Equipment list selected by the shared location operation. */
export function decodeEquipmentResources(
  locations: readonly Parameters<typeof parseEquipmentResource>[0][],
): ResourceResult<
  readonly EquipmentResource[],
  EquipmentResourceDomainFailure
> {
  const resources: EquipmentResource[] = []

  for (const location of locations) {
    if (
      location.type !== LocationType.OffloadingEquipment ||
      location.isDeleted
    ) {
      continue
    }

    const resource = parseEquipmentResource(location)
    if (resource._tag === 'Err') return resource
    resources.push(resource.value)
  }

  return resourceOk(resources)
}

/** Builds the Equipment mutation payload without executing effects. */
export function buildEquipmentSaveVariables(
  projectId: string,
  command: ResourceSaveCommand,
  resources: readonly EquipmentResource[],
  accessibleLevelsSource?: EquipmentStateSource | null,
): ResourceResult<
  { readonly equipments: readonly IOffloadingEquipmentInput[] },
  MissingEquipmentResourceRecord
> {
  const { recordId, formData } = command
  const formOverrides = splitResourceFormData(
    ResourceTypeId.OFFLOADING_EQUIPMENT,
    formData,
    ['operatingIntervals', 'permittedCompanies'],
  )
  const isCreation = recordId === 'new' || !recordId
  const existing = isCreation
    ? null
    : (resources.find(resource => resource.id === recordId) ?? null)

  if (!isCreation && !existing) {
    return resourceErr({
      _tag: 'MissingEquipmentResourceRecord',
      recordId,
    })
  }

  return resourceOk({
    equipments: [
      buildEquipmentInput(
        existing,
        projectId,
        isCreation ? null : recordId,
        formOverrides,
        accessibleLevelsSource,
      ),
    ],
  })
}

/** Creates the pure Equipment variation consumed by the shared resource store. */
export function createEquipmentResourceType(
  accessibleLevelsSource: EquipmentStateSource,
): ProjectResourceType<EquipmentResource, EquipmentResourceDomainFailure> {
  return {
    typeId: ResourceTypeId.OFFLOADING_EQUIPMENT,
    saveDocument: SaveEquipmentsDocument,
    decodeResources: decodeEquipmentResources,
    toRecord: toEquipmentResourceRecord,
    applyWorkflowRestriction: applyEquipmentWorkflowRestriction,
    buildSaveVariables: (projectId, command, resources) =>
      buildEquipmentSaveVariables(
        projectId,
        command,
        resources,
        accessibleLevelsSource,
      ),
  }
}
